import { createFileRoute } from '@tanstack/react-router';
import { Group, PasswordInput, Stack, Switch, TextInput } from '@mantine/core';
import { KeyRound, Settings, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { Badge, Button, PageContainer, PageHeader, Section } from '../components/ui';
import { staggerContainer, cardItem } from '../lib/motion';
import { useAuthStore } from '../store/useAuthStore';
import { toast } from '../store/useToasts';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const [mfa, setMfa] = useState(true);
  const [sso, setSso] = useState(false);
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  const region = (import.meta.env.VITE_COGNITO_REGION as string | undefined) ?? 'eu-central-1';

  const changePassword = async () => {
    if (!curPw || !newPw) { toast.error('Enter your current and new password'); return; }
    if (newPw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    if (!accessToken) { toast.error('Session expired — sign in again'); return; }
    setPwBusy(true);
    try {
      const resp = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
        },
        body: JSON.stringify({ AccessToken: accessToken, PreviousPassword: curPw, ProposedPassword: newPw }),
      });
      if (!resp.ok) {
        const d = (await resp.json()) as { message?: string; __type?: string };
        throw new Error(d.message || d.__type || `HTTP ${resp.status}`);
      }
      toast.success('Password changed');
      setCurPw(''); setNewPw(''); setConfirmPw('');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not change password');
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <PageContainer size="narrow">
      <PageHeader
        icon={Settings}
        title="Settings"
        description="Your account, theme and workspace preferences."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Settings' }]}
      />

      <motion.div variants={staggerContainer} initial="hidden" animate="show">
        <motion.div variants={cardItem}>
          <Section title="Profile" description="Your identity within this tenant. These values are managed by your administrator.">
            <Stack gap="md">
              <Group grow>
                <TextInput
                  label="Email"
                  value={user?.email ?? ''}
                  readOnly
                  styles={{ input: { fontFamily: "'JetBrains Mono', ui-monospace, monospace" } }}
                />
                <TextInput label="Tenant" value={user?.tenantId ?? ''} readOnly />
              </Group>
              <Group gap="sm">
                <Badge variant="info">{user?.role ?? 'guest'}</Badge>
              </Group>
            </Stack>
          </Section>
        </motion.div>

        <motion.div variants={cardItem}>
          <Section
            title="Security"
            description="Authentication and password controls for your account."
            action={<ShieldCheck className="size-4 text-emerald-500" />}
          >
            <Stack gap="md">
              <Switch
                label="Multi-factor authentication"
                description="TOTP via your authenticator app — required by the tenant policy."
                checked={mfa}
                onChange={(e) => setMfa(e.currentTarget.checked)}
              />
              <Switch
                label="Single sign-on (SAML)"
                description="Federate sign-in to your IdP. Configurable in Admin → Tenants."
                checked={sso}
                onChange={(e) => setSso(e.currentTarget.checked)}
                disabled
              />
              <PasswordInput label="Current password" placeholder="Your current password" value={curPw}
                onChange={(e) => setCurPw(e.currentTarget.value)} autoComplete="current-password" />
              <Group grow>
                <PasswordInput label="New password" placeholder="At least 8 characters" value={newPw}
                  onChange={(e) => setNewPw(e.currentTarget.value)} autoComplete="new-password" />
                <PasswordInput label="Confirm" placeholder="Re-type" value={confirmPw}
                  onChange={(e) => setConfirmPw(e.currentTarget.value)} autoComplete="new-password" />
              </Group>
              <Group gap="sm">
                <Button variant="primary" leftIcon={<KeyRound className="size-4" />}
                  onClick={() => void changePassword()} loading={pwBusy}>
                  Update password
                </Button>
              </Group>
            </Stack>
          </Section>
        </motion.div>

        <motion.div variants={cardItem}>
          <Section
            title="Notifications"
            description="Choose which emails Forge sends you."
            action={<ShieldAlert className="size-4 text-amber-500" />}
          >
            <Stack gap="md">
              <Switch
                label="Email me when a range hits 90% of its time budget"
                checked={emailAlerts}
                onChange={(e) => setEmailAlerts(e.currentTarget.checked)}
              />
              <Switch label="Email me weekly spend summaries" defaultChecked />
              <Switch label="Email me on new tenant-wide announcements" />
            </Stack>
          </Section>
        </motion.div>
      </motion.div>
    </PageContainer>
  );
}
